import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formControlClass } from "../../dashboardStyles";
import BrandingImageField from "../BrandingImageField";
import AboutBlocksEditor from "./about/AboutBlocksEditor";
import { initialsOf } from "../wizard/utils/initials";
import { ColorField, Field, PanelCard, PanelNote, labelTextClass } from "./siteFields";

/**
 * The five panels of the Site tab, one per section of the public page. Each is
 * a view of the same form state, owned by MySite: switching panels never loses
 * an edit, and one Save writes them all.
 */

const panelPropTypes = {
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const k = "Dashboard.MySite";

export const HeaderPanel = ({ formData, onChange, logoPreview, businessName, onOpenBrand }) => {
  const { t } = useTranslation();

  return (
    <PanelCard title={t(`${k}.header.title`)} subtitle={t(`${k}.header.subtitle`)}>
      <Field id="bookingTitle" label={t(`${k}.header.pageTitle`)} info={t(`${k}.header.pageTitleTip`)}>
        <input
          type="text"
          id="bookingTitle"
          name="bookingTitle"
          value={formData.bookingTitle}
          onChange={onChange}
          maxLength={500}
          className={formControlClass}
          placeholder={t(`${k}.header.pageTitlePlaceholder`)}
        />
      </Field>
      <Field id="heroDescription" label={t(`${k}.header.tagline`)} optional>
        <textarea
          id="heroDescription"
          name="heroDescription"
          rows={3}
          value={formData.heroDescription}
          onChange={onChange}
          maxLength={500}
          className={`${formControlClass} resize-y`}
          placeholder={t(`${k}.header.taglinePlaceholder`)}
        />
      </Field>
      <Field id="ctaButtonText" label={t(`${k}.header.ctaLabel`)} info={t(`${k}.header.ctaTip`)}>
        <input
          type="text"
          id="ctaButtonText"
          name="ctaButtonText"
          value={formData.ctaButtonText}
          onChange={onChange}
          maxLength={100}
          className={formControlClass}
          placeholder={t(`${k}.header.ctaPlaceholder`)}
        />
      </Field>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ink-200 bg-ink-50 p-3">
        <span
          className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ink-200 bg-white text-base font-bold text-brand-700"
          aria-hidden="true"
        >
          {logoPreview ? (
            <img src={logoPreview} alt="" className="max-h-full max-w-full object-contain p-1" />
          ) : (
            initialsOf(businessName) || <i className="bi bi-image text-ink-400" />
          )}
        </span>
        <span className={`${labelTextClass} min-w-0 flex-1`}>{t(`${k}.header.images`)}</span>
        <button
          type="button"
          onClick={onOpenBrand}
          className="inline-flex min-h-11 cursor-pointer items-center gap-1 rounded-lg px-2 text-sm font-semibold text-brand-700 transition hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
        >
          {t(`${k}.header.changeImages`)}
          <i className="bi bi-chevron-right text-xs" aria-hidden="true" />
        </button>
      </div>
    </PanelCard>
  );
};

HeaderPanel.propTypes = {
  ...panelPropTypes,
  logoPreview: PropTypes.string,
  businessName: PropTypes.string,
  onOpenBrand: PropTypes.func.isRequired,
};

export const AboutPanel = ({
  value,
  disabled,
  onBusyChange,
  onValueChange,
  selectedBlock,
  onSelectBlock,
  onStatusChange,
}) => {
  const { t } = useTranslation();

  return (
    <PanelCard title={t(`${k}.about.title`)} subtitle={t(`${k}.about.subtitle`)}>
      <AboutBlocksEditor
        value={value}
        disabled={disabled}
        onBusyChange={onBusyChange}
        onChange={onValueChange}
        selected={selectedBlock}
        onSelect={onSelectBlock}
        onStatusChange={onStatusChange}
      />
    </PanelCard>
  );
};

AboutPanel.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool,
  onBusyChange: PropTypes.func,
  onValueChange: PropTypes.func.isRequired,
  selectedBlock: PropTypes.number,
  onSelectBlock: PropTypes.func,
  onStatusChange: PropTypes.func,
};

const contactFields = [
  { name: "streetAddress", type: "text", maxLength: 500, autoComplete: "street-address" },
  { name: "bookingCity", type: "text", maxLength: 200, autoComplete: "address-level2" },
  { name: "contactPhone", type: "tel", maxLength: 50, autoComplete: "tel" },
  { name: "contactEmail", type: "email", maxLength: 255, autoComplete: "email" },
];

export const ContactPanel = ({ formData, onChange }) => {
  const { t } = useTranslation();

  return (
    <PanelCard title={t(`${k}.contact.title`)} subtitle={t(`${k}.contact.subtitle`)}>
      <div className="grid gap-5 sm:grid-cols-2">
        {contactFields.map(({ name, type, maxLength, autoComplete }) => (
          <Field key={name} id={name} label={t(`${k}.contact.${name}`)}>
            <input
              type={type}
              id={name}
              name={name}
              value={formData[name]}
              onChange={onChange}
              maxLength={maxLength}
              autoComplete={autoComplete}
              className={formControlClass}
              placeholder={t(`${k}.contact.${name}Placeholder`)}
            />
          </Field>
        ))}
      </div>
      <Field
        id="openingHours"
        label={t(`${k}.contact.hours`)}
        info={t(`${k}.contact.hoursTip`)}
        hint={t(`${k}.contact.hoursHint`)}
      >
        <textarea
          id="openingHours"
          name="openingHours"
          rows={4}
          value={formData.openingHours}
          onChange={onChange}
          maxLength={1000}
          className={`${formControlClass} resize-y`}
          placeholder={t(`${k}.contact.hoursPlaceholder`)}
        />
      </Field>
      <PanelNote>{t(`${k}.contact.emptyNote`)}</PanelNote>
    </PanelCard>
  );
};

ContactPanel.propTypes = panelPropTypes;

export const SocialPanel = ({ formData, onChange }) => {
  const { t } = useTranslation();

  return (
    <PanelCard title={t(`${k}.social.title`)} subtitle={t(`${k}.social.subtitle`)}>
      {[
        { name: "facebookUrl", icon: "bi-facebook" },
        { name: "instagramUrl", icon: "bi-instagram" },
      ].map(({ name, icon }) => (
        <Field key={name} id={name} label={t(`${k}.social.${name}`)}>
          <div className="relative">
            <i
              className={`bi ${icon} pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-400`}
              aria-hidden="true"
            />
            {/* The icon sits in the padding, so the control's own px-3 is
                replaced, not appended to: the two would tie. */}
            <input
              type="url"
              id={name}
              name={name}
              value={formData[name]}
              onChange={onChange}
              maxLength={500}
              inputMode="url"
              className="w-full rounded-lg border border-ink-200 bg-white py-2.5 pr-3 pl-9 text-fluid-sm text-ink-900 transition placeholder:text-ink-400 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20 focus:outline-none disabled:cursor-not-allowed disabled:bg-ink-50"
              placeholder={t(`${k}.social.${name}Placeholder`)}
            />
          </div>
        </Field>
      ))}
      <PanelNote>{t(`${k}.social.emptyNote`)}</PanelNote>
      <div className="flex items-center gap-3 rounded-xl bg-ink-50 px-4 py-3 text-sm text-ink-600">
        <i className="bi bi-link-45deg text-lg text-ink-500" aria-hidden="true" />
        <span className="min-w-0">{t(`${k}.social.newTab`)}</span>
      </div>
    </PanelCard>
  );
};

SocialPanel.propTypes = panelPropTypes;

export const BrandPanel = ({
  formData,
  onChange,
  logoFile,
  headerImageFile,
  disabled,
  onLogoChange,
  onHeaderChange,
  onLogoBusyChange,
  onHeaderBusyChange,
}) => {
  const { t } = useTranslation();

  return (
    <PanelCard title={t(`${k}.brand.title`)} subtitle={t(`${k}.brand.subtitle`)}>
      <BrandingImageField
        id="logoFile"
        kind="logo"
        label={t(`${k}.branding.logo`)}
        info={t(`${k}.brand.logoTip`)}
        hint={t(`${k}.branding.logoHint`)}
        file={logoFile}
        imageUrl={formData.logoUrl}
        removed={formData.removeLogo}
        disabled={disabled}
        onBusyChange={onLogoBusyChange}
        onChange={onLogoChange}
      />
      <BrandingImageField
        id="headerImageFile"
        kind="header"
        label={t(`${k}.branding.headerImage`)}
        info={t(`${k}.brand.headerImageTip`)}
        hint={t(`${k}.branding.headerImageHint`)}
        file={headerImageFile}
        imageUrl={formData.headerImageUrl}
        removed={formData.removeHeaderImage}
        disabled={disabled}
        onBusyChange={onHeaderBusyChange}
        onChange={onHeaderChange}
      />
      <div className="grid gap-5 border-t border-ink-100 pt-5 sm:grid-cols-2">
        <ColorField
          id="primaryColor"
          label={t(`${k}.branding.primaryColor`)}
          hint={t(`${k}.brand.primaryHint`)}
          value={formData.primaryColor}
          onChange={onChange}
        />
        <ColorField
          id="secondaryColor"
          label={t(`${k}.branding.secondaryColor`)}
          hint={t(`${k}.brand.secondaryHint`)}
          value={formData.secondaryColor}
          onChange={onChange}
        />
      </div>
    </PanelCard>
  );
};

BrandPanel.propTypes = {
  ...panelPropTypes,
  logoFile: PropTypes.object,
  headerImageFile: PropTypes.object,
  disabled: PropTypes.bool,
  onLogoChange: PropTypes.func.isRequired,
  onHeaderChange: PropTypes.func.isRequired,
  onLogoBusyChange: PropTypes.func,
  onHeaderBusyChange: PropTypes.func,
};
